import { talkPriceSubunitsFor } from './talkPrices';
/**
 * Shared listing-card copy: Display name (listing title), About you one-liner,
 * and From $lowest. Never invent bios — only surface what the listener wrote.
 *
 * Public-facing name rule:
 * - Listener: Listening profile screen name (listing.attributes.title)
 * - Talker: Talking profile screen name (their Talking listing title, or
 *   publicData.talkingDisplayName)
 * - Account First L (profile.displayName) only as fallback
 *
 * Mapping (do not add another name field):
 * - Details “Display name” → listing.attributes.title → card name, H1, Featured, edit headers
 * - Details “About you” → listing.attributes.description → profile body;
 *   first sentence → card one-liner
 * - Account first name → dashboard greeting only
 */
import { userDisplayNameAsString } from './data';
import { types as sdkTypes } from './sdkLoader';
import {
  SESSION_CURRENCY,
  SESSION_FORMAT_PRICES_SUBUNITS,
  offeredFormatsFromListing,
} from './sessionPricing';

const { Money } = sdkTypes;

const trimmed = value => (typeof value === 'string' ? value.trim() : '');

/**
 * First + last initial (Sam P). Used only to prefer a stored screen name
 * when the listing title was left as the account fallback.
 *
 * @param {string} [name]
 * @returns {boolean}
 */
export const looksLikeAccountFirstL = name => /^\S+\s+[A-Za-z]$/.test(trimmed(name));

/**
 * Listing title, then publicData.listeningDisplayName. Empty when neither
 * is a usable screen name.
 *
 * @param {Object} [listing]
 * @returns {string}
 */
export const listingCardDisplayName = listing => {
  const title = trimmed(listing?.attributes?.title || listing?.title);
  const listening = trimmed(
    listing?.attributes?.publicData?.listeningDisplayName || listing?.publicData?.listeningDisplayName
  );
  if (listening && (!title || looksLikeAccountFirstL(title))) {
    return listening;
  }
  return title || listening;
};

/**
 * Account First L. Fallback only — never the first choice on a public surface.
 *
 * @param {Object} [user]
 * @returns {string}
 */
export const accountDisplayName = user => userDisplayNameAsString(user, '') || '';

/**
 * A user's own role screen name: their profile listing title, then
 * talking/listening publicData, then account First L.
 *
 * @param {Object} [user]
 * @returns {string}
 */
export const userProfileScreenName = user => {
  if (!user) {
    return '';
  }
  const ownListing = user.profileListing || user.listing || user.authorListing;
  const ownTitle = listingCardDisplayName(ownListing);
  if (ownTitle) {
    return ownTitle;
  }
  const publicData = user.attributes?.profile?.publicData || {};
  const fromPublic =
    trimmed(publicData.talkingDisplayName) || trimmed(publicData.listeningDisplayName);
  if (fromPublic) {
    return fromPublic;
  }
  return accountDisplayName(user);
};

/**
 * Public name on a Listening or Talking profile. Listing title (screen name)
 * first. Account First L only when the listing has no title.
 *
 * @param {Object} [listing]
 * @param {Object} [author]
 * @returns {string}
 */
export const publicFacingName = (listing, author) =>
  listingCardDisplayName(listing) || userProfileScreenName(author || listing?.author);

/**
 * Public name on a Listening or Talking profile. Listing title (screen name)
 * first. Account First L only when the listing has no title.
 *
 * @param {Object} [listing]
 * @param {Object} [author]
 * @returns {string}
 */
export const talkerBookingDisplayName = (listing, author) => publicFacingName(listing, author);

/**
 * Other-party public name on talksession order/sale surfaces.
 * Talker (customer) sees the listener’s Listening listing title.
 * Listener (provider) sees the talker’s Talking screen name.
 *
 * @param {Object} [params]
 * @param {boolean} [params.isCustomer]
 * @param {Object} [params.listing]
 * @param {Object} [params.customer]
 * @param {Object} [params.provider]
 * @param {Object} [params.otherListing] talker’s Talking listing, when loaded
 * @returns {string}
 */
export const talkSessionOtherPartyDisplayName = ({
  isCustomer,
  listing,
  customer,
  provider,
  otherListing,
} = {}) => {
  if (isCustomer) {
    return publicFacingName(listing, provider);
  }
  return publicFacingName(
    otherListing || customer?.profileListing || customer?.listing,
    customer
  );
};

/**
 * First sentence of a user-written description.
 *
 * @param {string} [description]
 * @returns {string}
 */
export const firstDescriptionSentence = (description = '') => {
  const text = typeof description === 'string' ? description.trim() : '';
  if (!text) {
    return '';
  }
  const match = text.match(/^[^.!?\n]+[.!?]?/);
  return match ? match[0].trim() : text;
};

/**
 * Card one-liner: first sentence of About you. Never the listing title.
 *
 * @param {Object} [listing]
 * @returns {string}
 */
export const listingCardOneLiner = listing =>
  firstDescriptionSentence(listing?.attributes?.description);

/**
 * Lowest offered session price (text $10 / voice $20 / video $30).
 *
 * @param {Object} [listing]
 * @returns {Object|null} Sharetribe Money
 */
export const listingCardLowestPrice = listing => {
  const formats = offeredFormatsFromListing(listing);
  const amounts = formats
    .map(format => talkPriceSubunitsFor(listing, format) ?? SESSION_FORMAT_PRICES_SUBUNITS[format])
    .filter(amount => Number.isFinite(amount));
  if (amounts.length > 0) {
    return new Money(Math.min(...amounts), SESSION_CURRENCY);
  }
  return listing?.attributes?.price || null;
};

/**
 * Publish / draft rule: Display name plus About you.
 *
 * @param {Object} [values]
 * @returns {boolean}
 */
export const hasRequiredListeningOneLiner = (values = {}) => {
  const title = typeof values.title === 'string' ? values.title.trim() : '';
  const description = typeof values.description === 'string' ? values.description.trim() : '';
  return title.length > 0 && description.length > 0;
};

export const LISTENING_ONELINER_MIN = 1;
